import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Group, TopBar } from '@ekhodealer/ekho-common/rev';

import SplitBox24 from '@common/components/icons/SplitBox24';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useGetUserServicesProviders } from '@common/hooks/useGetUserServicesProviders';
import { paths } from '@config/paths';
import { setRecentServicesProviderId } from '@app/utils/getFirstServicesProviderId';

import { UserMenu } from './UserMenu';

/**
 * Max page-level path segments (including provider ID) per section.
 * When switching accounts on a detail page (order drawer, lead details, etc.),
 * segments beyond this depth are stripped so the user lands on the list page
 * instead of trying to load an entity that doesn't exist for the new provider.
 */
const SECTION_PAGE_DEPTH: Record<string, number> = {
	orders: 3, // /:spId/orders/:tabId
	preorders: 3, // /:spId/preorders/:tabId
	discounts: 3, // /:spId/discounts/:tabId
	leads: 3, // /:spId/leads/:tabId
	inventory: 2, // /:spId/inventory
	conversations: 2, // /:spId/conversations
};

export const AppTopBar: React.FC<{ onToggleSideNav: () => void }> = ({ onToggleSideNav }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const currentServicesProviderId = useCurrentServicesProviderId();
	const { data: currentServicesProvider } = useCurrentServicesProvider();
	const { servicesProviders } = useGetUserServicesProviders();
	const servicesProviderId = currentServicesProvider?.id;

	const handleLogoClick = (): void => {
		if (servicesProviderId) {
			navigate(paths.orders.getHref(servicesProviderId));
		}
	};

	const handleAccountSwitch = useCallback(
		(selectedId: string): void => {
			if (selectedId === currentServicesProviderId) return;

			setRecentServicesProviderId(selectedId);

			const pathSegments = location.pathname.split('/').filter(Boolean);

			if (pathSegments.length === 0) {
				navigate(`/${selectedId}/`);
				return;
			}

			// Replace the provider ID (first segment)
			pathSegments[0] = selectedId;

			// For sections with detail routes, strip entity-specific segments
			// so the user lands on the list/page level instead of a broken detail view
			const section = pathSegments[1];
			const maxDepth = section ? SECTION_PAGE_DEPTH[section] : undefined;
			const finalSegments =
				maxDepth != null && pathSegments.length > maxDepth
					? pathSegments.slice(0, maxDepth)
					: pathSegments;

			navigate(`/${finalSegments.join('/')}${location.search}${location.hash}`);
		},
		[currentServicesProviderId, location.pathname, location.search, location.hash, navigate],
	);

	// Create dropdown options from services providers
	const accountOptions = useMemo(() => {
		// Sort services providers alphabetically by name (or id if name is not available)
		const sortedProviders = [...servicesProviders].sort((a, b) => {
			const nameA = (a.name || a.id || '').toLowerCase();
			const nameB = (b.name || b.id || '').toLowerCase();
			return nameA.localeCompare(nameB);
		});

		const options = sortedProviders.map((sp) => {
			const isCurrentProvider = sp.id === servicesProviderId;
			const checkmark = isCurrentProvider ? '✓ ' : '';
			return {
				label: `${checkmark}${sp.name || sp.id}`,
				ariaLabel: `Switch to ${sp.name || sp.id}`,
				onPress: () => {
					handleAccountSwitch(sp.id);
				},
			};
		});

		return options;
	}, [servicesProviders, handleAccountSwitch, servicesProviderId]);

	// Get current account name for the label
	const currentAccount = servicesProviders.find((sp) => sp.id === servicesProviderId);
	const accountLabel = currentAccount?.name || servicesProviderId || 'Select Account';

	const topBarItems = [
		{
			id: 'accounts',
			location: 'right-side' as const,
			data: {
				type: 'DROPDOWN' as const,
				icon: null,
				label: accountLabel,
				ariaLabel: 'Switch Account',
				options: accountOptions,
			},
		},
	];

	return (
		<TopBar
			className='app-top-bar'
			onLogoClick={handleLogoClick}
			items={topBarItems}
			leftOfLogoContent={
				<Group itemsSpacing='8' itemsAlignY='center' noWrap>
					<Button
						ariaLabel='Toggle side navigation'
						variant='secondary'
						size='medium'
						icon={<SplitBox24 />}
						onPress={onToggleSideNav}
					/>
				</Group>
			}
			moreButton={<UserMenu />}
		/>
	);
};
